import React, {
    useEffect, useState, useContext,
} from 'react';
import { Box } from '@mui/material';
import { ColumnDefs } from '../../../../types/AgGridTypes';
import AgGridComponent from '../../../../components/agGridTableComponent';
import wbsQueries from '../../../../queries/wbs';
import { useGQLQuery } from '../../../../hooks/useGQLQuery';
import Loader from '../../../../components/Loader';
import { DEFAULT_PAGE_SIZE } from '../../../../constants';
import Pagination from '../../../../components/paginationTable';
import { UserContext } from '../../../../store/context/userContext';
import { ProjectContext } from '../../../../store/context/projectContext';
import { useOutletContext } from 'react-router-dom';
import { OutletContext, QueryErrorResponse } from '../../../../types/OrganisationDetailType';
import WbsCustom from './WbsCustom';
import { JSONParse } from '../../../../helper/StorageHelper';
import { truthyCodeValidation } from '../../../../helper/validations';
import SearchIcon from '@mui/icons-material/Search';
import projectCs from '../../../../queries/case';

interface CaseRenderer {
    labour_hoursTotal: number,
    labourTotal: number,
    distributableTotal: number,
    c_equipmentTotal: number,
    materialTotal: number,
    sustaining_total: number,
    equipmentTotal: number,
    subcontractTotal: number,
    indirectTotal: number,
    total_costTotal: number,
    name: string,
    total_cost: string | number,
}

type CaseObject = {
    description: string;
    [key: string]: string | number;
};

interface Props {
    view?: string,
    searchText: string
    setSearchText?: React.Dispatch<React.SetStateAction<string>>;
    hideUnusedFlag: boolean;
    setIsFetching: React.Dispatch<React.SetStateAction<boolean>>;

}

interface WbsColumnDefs extends ColumnDefs {
    sort?: string,
    pinned?: string,
    level?: string,
}

export default function WBSCaseView(props:Props) {
    const {
        view, searchText, setSearchText, hideUnusedFlag, setIsFetching,
    } = props;
    const [pageSkipValue, setPageSkipValue] = useState(0);
    const [newColumnDefs, setNewColumnDefs] = useState<ColumnDefs[]>([]);
    const [colHeader, setColHeader] = useState<string[]>([]);
    const cont: OutletContext = useOutletContext();
    const userCtx = useContext(UserContext);
    const projectCtx = useContext(ProjectContext);
    // const [searchText, setSearchText] = useState('');

    const displayedCurrencyId = projectCtx?.project?.currency_id;
    const displayCurr = projectCtx?.projectCurrencyData?.getprojectCurrency?.find((curr) => curr.id === displayedCurrencyId);
    const exchangeRate = displayCurr?.exchange_rate || 1;

    const handleApiError = ({ response }: QueryErrorResponse) => {
        const message = response && response.errors && response.errors[0] ? response.errors[0].message : 'API failed';
        cont.showNotificationBar(message, 'error');
    };

    const createCaseData = (params: CaseRenderer[]) => {
        const result: CaseObject = {
            description: 'TOTAL',
        };
        for (let i = 0; i < params.length; i += 1) {
            result[`column_${params[i].name}`] = params[i].total_cost && (+(params[i].total_cost) / exchangeRate).toFixed(0);
        }
        return [result];
    };

    const projectId = projectCtx?.project?.id || '';

    const { data: GetCaseData, isFetching, refetch } = useGQLQuery(
        `GetWbsCase-${projectId || 0}`,
        wbsQueries.GET_WBS_CASE_INFO(
            pageSkipValue,
            DEFAULT_PAGE_SIZE,
            userCtx?.user?.default_org_id || '',
            projectCtx?.project?.id || '',
            projectCtx?.project?.version_id || '',
            hideUnusedFlag,
        ),
        {},
        { onError: handleApiError },
    );
    const { data: projectCase, refetch: isCaseFetching } = useGQLQuery(
        'GetProjectCase',
        projectCs.GET_PROJECT_CASE(
            userCtx?.user?.default_org_id || '',
            projectCtx?.project?.id || '',
            projectCtx?.project?.version_id || '',
        ),
        {},
        {
            onError: handleApiError,
        },
        '/setting',
    );
    // For WBS Case view...
    const caseViewData = GetCaseData?.wbscase;
    const tableCaseData = caseViewData?.data;
    const tableCaseViewData: CaseObject[] = tableCaseData ? JSONParse(tableCaseData.toString()) : [];
    const casePageInfo = caseViewData?.pageInfo;
    const caseTotalValue: CaseRenderer[] = caseViewData?.total_values && caseViewData?.total_values !== undefined
        ? JSONParse(caseViewData?.total_values.toString()) : [];
    // For Bottom Total Value..
    let pinnedBottomRowData;
    if (caseTotalValue && caseTotalValue.length > 0) {
        pinnedBottomRowData = createCaseData(caseTotalValue);
    }

    useEffect(() => {
        refetch();
    }, [pageSkipValue, hideUnusedFlag]);

    const columnDefs: WbsColumnDefs[] = [
        {
            field: 'code',
            headerName: 'WBS',
            initialWidth: 130,
            cellRenderer: WbsCustom,
            type: 'string',
            sort: 'asc',
        },
        {
            field: 'description',
            headerName: 'Title',
            cellRenderer: WbsCustom,
            initialHide: view !== 'Case View',
            initialWidth: 400,
            type: 'string',
            enableValue: false,
        },
    ];

    const setCaseViewFunc = () => {
        if (projectCase && projectCase.case && view === 'Case View') {
            setColHeader([]);
            const tempArr: string[] = [];
            const colArr: WbsColumnDefs[] = [];
            projectCase.case.forEach((item) => {
                colArr.push(
                    {
                        field: `column_${item.name}`,
                        headerName: item.name,
                        cellRenderer: WbsCustom,
                        // sorting: true,
                        initialHide: view !== 'Case View',
                        initialWidth: 150,
                        type: 'numericColumn',
                        enableValue: true,
                        level: item.code,
                    },
                );
            });
            colArr.sort((a, b) => {
                if (a.level && b.level) {
                    if (a.level < b.level) return -1;
                    if (a.level > b.level) return 1;
                }
                return 0;
            });
            colArr.forEach((col) => {
                columnDefs.push(col);
                tempArr.push(`${col?.field}`);
                setNewColumnDefs(columnDefs);
                setNewColumnDefs(columnDefs);
            });
            setColHeader([...colHeader, ...tempArr]);
        }
    };

    const generateColumnDefs = () => {
        const secendNewColumnDefs = [...columnDefs];
        ['code1', 'code2', 'code3', 'code4', 'code5', 'code6'].forEach((code, index) => {
            const flag = (tableCaseViewData?.map(
                (item) => (
                    (item)[code]),
            ))?.some(truthyCodeValidation);
            if (flag) {
                secendNewColumnDefs.push({
                    field: code,
                    headerName: `Code${index + 1}`,
                    initialWidth: 130,
                    cellRenderer: WbsCustom,
                    type: 'string',
                    initialHide: flag,
                    enableValue: false,
                });
            }
        });
        setNewColumnDefs(secendNewColumnDefs);
    };

    useEffect(() => {
        setCaseViewFunc();
        generateColumnDefs();
    }, [view, isFetching]);

    useEffect(() => {
        setIsFetching(isFetching);
    }, [isFetching]);

    const handlePageChange = (page: number) => {
        // Fetch new page data
        const skipValue = (page - 1) * DEFAULT_PAGE_SIZE;
        setPageSkipValue(skipValue);
    };

    const totalPages = (casePageInfo && casePageInfo.totalcount) ? Math.ceil(casePageInfo.totalcount / DEFAULT_PAGE_SIZE) : 0;

    return (
        <div>
            {/* Page Header Start */}
            <Loader loading={isFetching} />
            <Box>
                <AgGridComponent
                    columnDefs={newColumnDefs}
                    rowData={tableCaseViewData}
                    changeSortingValue={() => {}}
                    pinnedBottomRowData={pinnedBottomRowData}
                    WbsRowBgStyle
                    isClipboardEnabled
                    isExportEnabled
                    isRangeSelectable
                    isToolPanelsEnabled
                    isStatusBarEnabled
                    isGroupable
                    moduleName="wbs_caseview"
                    colFormat={colHeader}
                    quickFilterText={searchText}
                    setSearchText={setSearchText}
                />
            </Box>
        </div>
    );
}
